import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../utils/supabase';

function snakeToCamel(str) {
  return str.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
}

function snakeToLabel(str) {
  return str
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .replace(/\bId\b/g, 'ID')
    .replace(/\bUrl\b/g, 'URL');
}

const SKIP_COLUMNS = new Set(['created_at', 'updated_at', 'search_terms']);

const COLUMN_CATEGORIES = {
  crm_contacts: {
    'Personal Info': ['first_name', 'last_name', 'middle_initial', 'email', 'phone', 'secondary_email', 'secondary_phone', 'birthday', 'company', 'job_title', 'website', 'preferred_contact_method'],
    'Address': ['mailing_street', 'mailing_city', 'mailing_state', 'mailing_zip', 'office_address'],
    'Classification': ['source', 'contact_types', 'primary_contact_type', 'tags', 'is_active', 'is_favorite', 'do_not_contact', 'owner_id'],
    'Licensing': ['license_number', 'license_state'],
  },
  crm_deals: {
    'Deal Info': ['deal_name', 'stage', 'amount', 'product', 'lender', 'loan_number', 'loan_amount', 'owner_id'],
    'Property': ['property_street', 'property_city', 'property_state', 'property_zip', 'property_type', 'occupancy_type'],
    'Loan Details': ['loan_purpose', 'purchase_or_refi', 'credit_score', 'down_payment', 'interest_rate', 'loan_term'],
    'Dates': ['estimated_closing_date', 'app_date', 'next_action_due', 'sla_timestamp'],
    'Source': ['source_medium', 'source_campaign', 'source_term', 'form_name'],
    'Status': ['is_at_risk', 'next_action', 'arive_id', 'tags'],
  },
};

/**
 * Loads column metadata from Supabase for crm_contacts and crm_deals
 * via the get_table_schema RPC. Caches results for the session.
 *
 * Returns grouped variable lists ready for the DynamicVariablePicker.
 */
export function useSchemaDiscovery() {
  const [contactColumns, setContactColumns] = useState([]);
  const [dealColumns, setDealColumns] = useState([]);
  const [loading, setLoading] = useState(true);
  const loadedRef = useRef(false);

  const loadSchema = useCallback(async () => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    setLoading(true);

    try {
      const [contactRes, dealRes] = await Promise.all([
        supabase.rpc('get_table_schema', { p_table_name: 'crm_contacts' }),
        supabase.rpc('get_table_schema', { p_table_name: 'crm_deals' }),
      ]);

      const mapColumns = (data, tableName) =>
        (data || [])
          .filter((c) => !SKIP_COLUMNS.has(c.column_name))
          .map((c) => ({
            dbColumn: c.column_name,
            camelKey: snakeToCamel(c.column_name),
            label: snakeToLabel(c.column_name),
            dataType: c.data_type,
            nullable: c.is_nullable === 'YES',
            table: tableName,
          }));

      setContactColumns(mapColumns(contactRes.data, 'crm_contacts'));
      setDealColumns(mapColumns(dealRes.data, 'crm_deals'));
    } catch (err) {
      console.warn('[useSchemaDiscovery] RPC failed, using fallback:', err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSchema();
  }, [loadSchema]);

  const getContactVariableGroups = useCallback(() => {
    const categories = COLUMN_CATEGORIES.crm_contacts;
    const groups = [];
    const assigned = new Set();

    for (const [groupLabel, columnNames] of Object.entries(categories)) {
      const vars = columnNames
        .map((cn) => contactColumns.find((c) => c.dbColumn === cn))
        .filter(Boolean)
        .map((c) => ({
          key: `contact.${c.camelKey}`,
          label: c.label,
          dataType: c.dataType,
        }));
      if (vars.length) {
        groups.push({ label: groupLabel, variables: vars });
        columnNames.forEach((cn) => assigned.add(cn));
      }
    }

    const uncategorized = contactColumns
      .filter((c) => !assigned.has(c.dbColumn) && c.dbColumn !== 'id')
      .map((c) => ({
        key: `contact.${c.camelKey}`,
        label: c.label,
        dataType: c.dataType,
      }));
    if (uncategorized.length) {
      groups.push({ label: 'Other Contact Fields', variables: uncategorized });
    }

    return groups;
  }, [contactColumns]);

  const getDealVariableGroups = useCallback(() => {
    const categories = COLUMN_CATEGORIES.crm_deals;
    const groups = [];
    const assigned = new Set();

    for (const [groupLabel, columnNames] of Object.entries(categories)) {
      const vars = columnNames
        .map((cn) => dealColumns.find((c) => c.dbColumn === cn))
        .filter(Boolean)
        .map((c) => ({
          key: `deal.${c.camelKey}`,
          label: c.label,
          dataType: c.dataType,
        }));
      if (vars.length) {
        groups.push({ label: groupLabel, variables: vars });
        columnNames.forEach((cn) => assigned.add(cn));
      }
    }

    const uncategorized = dealColumns
      .filter((c) => !assigned.has(c.dbColumn) && c.dbColumn !== 'id' && c.dbColumn !== 'contact_id')
      .map((c) => ({
        key: `deal.${c.camelKey}`,
        label: c.label,
        dataType: c.dataType,
      }));
    if (uncategorized.length) {
      groups.push({ label: 'Other Deal Fields', variables: uncategorized });
    }

    return groups;
  }, [dealColumns]);

  return {
    contactColumns,
    dealColumns,
    loading,
    getContactVariableGroups,
    getDealVariableGroups,
  };
}
